import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Box } from "@mui/material";
import { Add } from "@mui/icons-material";

import { PageLayout } from "../../layouts/PageLayout";
import { ActionCard } from "../../components/report";
import { AddEditUser, UsersList } from "../../components/administration";
import { DeleteModal, PageHeader } from "../../components/v3";
import { CREATE_ROLE, CREATE_USER, DELETE_ROLE, EDIT_ROLE, VIEW_ROLE, VIEW_USER } from "../../constants";
import { RolesService } from "../../services";
import { useHasPermission } from "../../utils";
import { usePersistedPageValue } from "../../hooks/usePersistedTableState";

const ADMINISTRATION_TABS = ["users", "roles"];
const isAdministrationTab = (value: unknown): value is string => typeof value === "string" && ADMINISTRATION_TABS.includes(value);

export const Administration = () => {
  const location = useLocation();
  const [roles, setRoles] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [reload, setReload] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  // Remembered per visit so Back from a user or role returns to the tab the user was on.
  const [activeTab, setActiveTab] = usePersistedPageValue("tab", location.state?.activeTab || "users", isAdministrationTab);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    roleId: null,
  });

  const navigate = useNavigate();

  const hasViewUser = useHasPermission(VIEW_USER);
  const hasViewRole = useHasPermission(VIEW_ROLE);
  const hasUserCreate = useHasPermission(CREATE_USER);
  const hasRoleCreate = useHasPermission(CREATE_ROLE);
  const hasRoleEdit = useHasPermission(EDIT_ROLE);
  const hasRoleDelete = useHasPermission(DELETE_ROLE);

  useEffect(() => {
    if (activeTab === "roles") {
      fetchData();
    }
  }, [activeTab]);

  const fetchData = async () => {
    try {
      const { data: response } = await RolesService.getRoles({ page: 1, limit: 100, sortBy: "updatedAt", sortOrder: "desc" });
      setRoles(response?.rows);
      setTotalCount(response?.count);
    } catch (error) {
      toast.error("Failed to fetch roles");
      console.log("🚀 ~ fetchData ~ error:", error);
    }
  };

  const handleOpenUserModal = () => setOpenModal(true);
  const handleCloseModal = () => setOpenModal(false);

  const handleAddRole = () => {
    navigate("/administration/roles/add", { state: { fromTab: "roles" } });
  };

  const handleOpenRoleModal = (role) => {
    navigate(`/administration/roles/edit/${role?.id}`, { state: { fromTab: "roles" } });
  };

  const openDeleteModal = (role) => {
    setDeleteModal({
      isOpen: true,
      roleId: role?.id,
    });
  };

  const closeDeleteModal = () => {
    setDeleteModal({
      isOpen: false,
      roleId: null,
    });
  };

  const handleDelete = async () => {
    try {
      await RolesService.deleteRole(deleteModal?.roleId);
      closeDeleteModal();
      toast.success("Role deleted successfully");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete role");
      console.error("🚀 ~ handleDelete ~ error:", error);
    }
  };

  const handleReload = () => setReload((prev) => prev + 1);

  const renderActiveTab = () => {
    switch (activeTab) {
      case "users":
        return <UsersList totalCount={totalCount} setTotalCount={setTotalCount} onSuccess={handleReload} reloadData={reload} />;
      case "roles":
        return (
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "flex-start",
              paddingY: "22px",
              paddingLeft: "22px",
              paddingRight: "8px",
              gap: 2,
              width: "100%",
            }}
          >
            {roles.map((item, index) => (
              <Box
                key={index}
                sx={{
                  flex: `0 0 calc(${100 / cardsPerRow}% - 16px)`,
                  boxSizing: "border-box",
                  "@media (max-width: 900px)": {
                    flex: "0 0 calc(50% - 16px)",
                  },
                  "@media (max-width: 600px)": {
                    flex: "0 0 100%",
                  },
                }}
              >
                <ActionCard
                  data={item}
                  {...item}
                  hasEditButton={hasRoleEdit}
                  hasDeleteButton={hasRoleDelete}
                  onEdit={handleOpenRoleModal}
                  onDelete={openDeleteModal}
                />
              </Box>
            ))}
          </Box>
        );
      default:
        return null;
    }
  };

  const buttons = [
    hasUserCreate &&
      activeTab === "users" && {
        buttonText: "Add User",
        buttonIcon: Add,
        onClick: handleOpenUserModal,
      },
    hasRoleCreate &&
      activeTab === "roles" && {
        buttonText: "Add Role",
        buttonIcon: Add,
        onClick: handleAddRole,
      },
  ];
  const administrationSections = [
    hasViewUser && { label: "Users", onClick: () => setActiveTab("users") },
    hasViewRole && { label: "Roles", onClick: () => setActiveTab("roles") },
  ].filter(Boolean);

  const cardsPerRow = 4;

  return (
    <>
      <PageLayout>
        {/* header section */}
        <Box sx={{ position: "relative", zIndex: 1 }}>
          <PageHeader
            // title
            title={`${activeTab === "users" ? "Users" : "Roles"} (${totalCount})`}
            // button
            buttons={buttons}
            // tabs
            chipItems={administrationSections}
            activeTab={activeTab}
          />
        </Box>

        <Box sx={{ mt: -7, zIndex: 2, position: "relative" }}>{renderActiveTab()}</Box>
      </PageLayout>

      {/* add user modal */}
      <AddEditUser open={openModal} onClose={handleCloseModal} onSuccess={handleReload} />

      {/* delete role modal */}
      <DeleteModal
        isOpen={deleteModal.isOpen}
        onClose={closeDeleteModal}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this role?"
      />
    </>
  );
};
